import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Camera, Mail, MessageCircle, Sparkles } from 'lucide-react'
import { HeroLogo } from '@/components/comingSoon/ComingSoonAmbience'
import { COMING_SOON_CONTENT, getLaunchProgress } from '@/lib/comingSoon'
import { useStoreGate } from '@/contexts/StoreGateContext'

export function RotatingHighlight() {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % COMING_SOON_CONTENT.highlights.length)
    }, 4200)
    return () => window.clearInterval(id)
  }, [])

  return (
    <div className="relative mx-auto mt-4 h-8 max-w-lg overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.p
          key={index}
          initial={{ opacity: 0, y: 18, filter: 'blur(6px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          exit={{ opacity: 0, y: -18, filter: 'blur(6px)' }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] as const }}
          className="absolute inset-x-0 font-display text-sm italic text-gold-300/80 sm:text-base"
        >
          {COMING_SOON_CONTENT.highlights[index]}
        </motion.p>
      </AnimatePresence>
    </div>
  )
}

export function LaunchProgressBar() {
  const { schedule } = useStoreGate()
  const [progress, setProgress] = useState(() => getLaunchProgress(schedule))

  useEffect(() => {
    const tick = () => setProgress(getLaunchProgress(schedule))
    tick()
    const id = window.setInterval(tick, 60_000)
    return () => window.clearInterval(id)
  }, [schedule.launchDate, schedule.progressStartDate])

  return (
    <div className="mt-6 w-full">
      <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
        <span>Preparing your experience</span>
        <span className="text-gold-400/80">{Math.round(progress)}%</span>
      </div>
      <div className="coming-soon-progress-track relative h-1.5 overflow-hidden rounded-full">
        <motion.div
          className="coming-soon-progress-fill absolute inset-y-0 left-0 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] as const }}
        />
        <div className="coming-soon-progress-shine absolute inset-0" aria-hidden />
      </div>
    </div>
  )
}

export function NotesTicker() {
  const items = [...COMING_SOON_CONTENT.notes, ...COMING_SOON_CONTENT.notes]

  return (
    <div className="mt-8 w-full max-w-xl sm:max-w-2xl">
      <p className="mb-2 text-center text-[10px] uppercase tracking-[0.25em] text-zinc-600">
        Featured collections
      </p>
      <div className="coming-soon-ticker-wrap relative overflow-hidden rounded-full border border-gold-500/10 bg-luxury-900/40 py-2.5">
        <div className="coming-soon-ticker flex w-max gap-6 px-4">
          {items.map((note, i) => (
            <span
              key={`${note}-${i}`}
              className="flex shrink-0 items-center gap-6 font-display text-sm text-gold-400/50"
            >
              {note}
              <span className="text-gold-600/25" aria-hidden>·</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

function normalizeInstagramUrl(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('http')) return trimmed
  const handle = trimmed.replace(/^@/, '')
  return `https://instagram.com/${handle}`
}

function normalizeWhatsAppUrl(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('http')) return trimmed
  const digits = trimmed.replace(/\D/g, '')
  if (!digits) return ''
  return `https://wa.me/${digits}`
}

export function ComingSoonSocial() {
  const { settings } = useStoreGate()

  const instagram = normalizeInstagramUrl(settings?.instagram ?? '')
  const whatsapp = normalizeWhatsAppUrl(settings?.whatsapp ?? '')

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.4, duration: 0.5 }}
      className="mt-8 flex flex-wrap items-center justify-center gap-3"
    >
      <span className="text-[10px] uppercase tracking-widest text-zinc-600">Stay connected</span>
      <Link
        to="/contact"
        className="coming-soon-social-btn group inline-flex items-center gap-2 rounded-full border border-gold-500/20 bg-gold-500/5 px-4 py-2 text-xs text-gold-300 transition hover:border-gold-400/40 hover:bg-gold-500/10"
      >
        <Mail className="h-3.5 w-3.5 text-gold-400 transition group-hover:scale-110" />
        Contact
      </Link>
      {instagram && (
        <a
          href={instagram}
          target="_blank"
          rel="noopener noreferrer"
          className="coming-soon-social-btn group inline-flex items-center gap-2 rounded-full border border-gold-500/20 bg-gold-500/5 px-4 py-2 text-xs text-gold-300 transition hover:border-gold-400/40 hover:bg-gold-500/10"
        >
          <Camera className="h-3.5 w-3.5 text-gold-400 transition group-hover:scale-110" />
          Instagram
        </a>
      )}
      {whatsapp && (
        <a
          href={whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className="coming-soon-social-btn group inline-flex items-center gap-2 rounded-full border border-gold-500/20 bg-gold-500/5 px-4 py-2 text-xs text-gold-300 transition hover:border-gold-400/40 hover:bg-gold-500/10"
        >
          <MessageCircle className="h-3.5 w-3.5 text-gold-400 transition group-hover:scale-110" />
          WhatsApp
        </a>
      )}
    </motion.div>
  )
}

const LAUNCH_REDIRECT_SECONDS = 5

const launchBurstParticles = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  angle: (i / 24) * 360,
  distance: 100 + (i % 6) * 28,
  delay: (i % 8) * 0.06,
  size: 3 + (i % 4) * 2,
}))

const viewportSparkles = Array.from({ length: 14 }, (_, i) => ({
  id: i,
  left: `${8 + ((i * 17) % 84)}%`,
  top: `${12 + ((i * 23) % 76)}%`,
  delay: i * 0.18,
  duration: 2.4 + (i % 3) * 0.6,
}))

export function LaunchLiveReveal() {
  const navigate = useNavigate()
  const { endLaunchCelebration } = useStoreGate()
  const [secondsLeft, setSecondsLeft] = useState(LAUNCH_REDIRECT_SECONDS)
  const redirectingRef = useRef(false)
  const redirectProgress =
    ((LAUNCH_REDIRECT_SECONDS - secondsLeft) / LAUNCH_REDIRECT_SECONDS) * 100

  useEffect(() => {
    void import('@/store/pages/HomePage')
  }, [])

  useEffect(() => {
    if (secondsLeft > 0) return
    if (redirectingRef.current) return
    redirectingRef.current = true

    void import('@/store/pages/HomePage').then(() => {
      endLaunchCelebration()
      navigate('/', { replace: true })
    })
  }, [secondsLeft, navigate, endLaunchCelebration])

  useEffect(() => {
    if (secondsLeft <= 0) return
    const id = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => window.clearTimeout(id)
  }, [secondsLeft])

  const rings = useMemo(() => [0, 1, 2, 3], [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] as const }}
      className="coming-soon-live-screen relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-10 sm:px-6"
    >
      {viewportSparkles.map(({ id, left, top, delay, duration }) => (
        <motion.span
          key={id}
          className="coming-soon-live-float-spark pointer-events-none absolute rounded-full bg-gold-300/80"
          style={{ left, top, width: 4, height: 4 }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 0.9, 0], scale: [0, 1.4, 0] }}
          transition={{ duration, delay, repeat: Infinity, repeatDelay: 0.4 }}
          aria-hidden
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] as const }}
        className="coming-soon-live-card relative w-full max-w-lg overflow-hidden rounded-2xl px-6 py-10 text-center sm:max-w-xl sm:px-10 sm:py-12"
      >
        <div className="coming-soon-live-glow pointer-events-none absolute inset-0" aria-hidden />
        <div className="coming-soon-live-card-shine pointer-events-none absolute inset-0" aria-hidden />

        {rings.map((ring) => (
          <motion.span
            key={ring}
            className="coming-soon-live-ring pointer-events-none absolute left-1/2 top-[42%] rounded-full border border-gold-400/35"
            initial={{ width: 48, height: 48, x: '-50%', y: '-50%', opacity: 0.65 }}
            animate={{ width: 420, height: 420, opacity: 0 }}
            transition={{
              duration: 2.6,
              delay: ring * 0.5,
              repeat: Infinity,
              ease: 'easeOut',
            }}
            aria-hidden
          />
        ))}

        {launchBurstParticles.map(({ id, angle, distance, delay, size }) => {
          const rad = (angle * Math.PI) / 180
          const x = Math.cos(rad) * distance
          const y = Math.sin(rad) * distance
          return (
            <motion.span
              key={id}
              className="coming-soon-live-spark pointer-events-none absolute left-1/2 top-[42%] rounded-full bg-gold-100"
              style={{ width: size, height: size }}
              initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
              animate={{ x, y, opacity: [0, 1, 0], scale: [0, 1.3, 0.3] }}
              transition={{
                duration: 1.8,
                delay,
                repeat: Infinity,
                repeatDelay: 0.6,
                ease: [0.22, 1, 0.36, 1] as const,
              }}
              aria-hidden
            />
          )
        })}

        <div className="relative z-10">
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 16, delay: 0.1 }}
          >
            <HeroLogo />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="coming-soon-live-badge mb-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.35em] text-gold-100 sm:text-xs"
          >
            <Sparkles className="h-3.5 w-3.5 text-gold-300" />
            Now Live
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.55 }}
            className="coming-soon-live-title font-display text-4xl font-bold sm:text-5xl md:text-6xl"
          >
            We&apos;re Live!
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-3 font-display text-base italic text-gold-200/75 sm:text-lg"
          >
            Bazm-e-Musk is officially open
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.62, duration: 0.5 }}
            className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-zinc-400"
          >
            Your curated attar experience awaits — luxury fragrances, crafted for the soul.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.78, duration: 0.5 }}
            className="relative z-10 mx-auto mt-8 flex w-fit flex-col items-center"
          >
            <p className="mb-3 text-[10px] uppercase tracking-[0.24em] text-zinc-500">
              Redirecting to home page in
            </p>

            <div className="coming-soon-live-countdown-ring relative flex h-28 w-28 items-center justify-center sm:h-32 sm:w-32">
              <svg
                className="absolute inset-0 -rotate-90"
                viewBox="0 0 120 120"
                aria-hidden
              >
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="rgba(212,175,55,0.15)"
                  strokeWidth="4"
                />
                <motion.circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="url(#liveCountdownGradient)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 52}
                  animate={{
                    strokeDashoffset:
                      2 * Math.PI * 52 * (1 - redirectProgress / 100),
                  }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                />
                <defs>
                  <linearGradient id="liveCountdownGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#c5a059" />
                    <stop offset="50%" stopColor="#f9e076" />
                    <stop offset="100%" stopColor="#d4af37" />
                  </linearGradient>
                </defs>
              </svg>

              <AnimatePresence mode="popLayout">
                <motion.span
                  key={secondsLeft}
                  initial={{ y: 14, opacity: 0, scale: 0.8 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={{ y: -14, opacity: 0, scale: 1.15 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] as const }}
                  className="coming-soon-live-count font-display text-5xl font-bold tabular-nums text-gold-50 sm:text-6xl"
                >
                  {secondsLeft}
                </motion.span>
              </AnimatePresence>
            </div>

            <p className="mt-3 text-xs text-zinc-500">
              {secondsLeft === 1 ? 'second' : 'seconds'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.95, duration: 0.5 }}
            className="mt-6 inline-flex items-center gap-2 text-xs text-gold-400/75"
          >
            <Sparkles className="h-3.5 w-3.5 animate-pulse text-gold-400" />
            Opening the storefront for you
          </motion.div>
        </div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.5 }}
        className="mt-8 text-center text-[10px] uppercase tracking-[0.22em] text-zinc-600 sm:text-xs"
      >
        {COMING_SOON_CONTENT.footerTagline}
      </motion.p>
    </motion.div>
  )
}

export function ArabicWatermark() {
  return (
    <div
      className="coming-soon-watermark pointer-events-none absolute left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2 select-none font-display text-[6rem] leading-none text-gold-500/[0.03] sm:text-[8rem]"
      aria-hidden
    >
      بزم
    </div>
  )
}
